import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../ui/sonner';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { ShieldCheck } from 'lucide-react';
import { readCurrentUser } from '../../lib/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LoginAccountsPanel = () => {
  const me = readCurrentUser();
  const [users, setUsers] = useState([]);
  const [pendingRole, setPendingRole] = useState(null); // { user, role }

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounts/users`);
      setUsers(res.data);
    } catch (err) {
      console.error('accounts/users failed', err);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const applyRole = async (user, role) => {
    try {
      await axios.put(`${API_URL}/api/accounts/users/${user.id}/role`, { role });
      toast.success(`${user.name} is now ${role.toUpperCase()}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Role change failed');
    }
  };

  const handleRoleSelect = (user, role) => {
    if (role === user.role) return;
    if (role === 'admin') {
      setPendingRole({ user, role }); // promoting to ADMIN requires confirmation
    } else {
      applyRole(user, role);
    }
  };

  const toggleActive = async (user) => {
    try {
      await axios.put(`${API_URL}/api/accounts/users/${user.id}/status`, { is_active: !user.is_active });
      toast.success(`${user.name} ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <Card className="p-4 sm:p-5" data-testid="login-accounts-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <h2 className="text-base font-semibold">Login Accounts &amp; Roles</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Admin-only role control. Roles are stored in the database and are never changed by deployments or restarts. Every change is audited.
      </p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Account</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Linked employee</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const isSelf = u.id === me?.id;
              return (
                <TableRow key={u.id} data-testid={`account-row-${u.email}`}>
                  <TableCell>
                    <div className="font-medium text-sm">{u.name}{isSelf && <span className="text-xs text-muted-foreground"> (you)</span>}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(role) => handleRoleSelect(u, role)} disabled={isSelf}>
                      <SelectTrigger className="w-[110px] h-8 bg-background" data-testid={`role-select-${u.email}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="admin">ADMIN</SelectItem>
                        <SelectItem value="staff">STAFF</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.is_active ? 'default' : 'secondary'} data-testid={`account-status-${u.email}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {u.force_password_change && <div className="text-[10px] text-muted-foreground mt-0.5">setup pending</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {u.team_member_id ? 'Linked' : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => toggleActive(u)} disabled={isSelf} data-testid={`account-toggle-${u.email}`}>
                      {u.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!pendingRole} onOpenChange={(o) => !o && setPendingRole(null)}>
        <AlertDialogContent data-testid="admin-promote-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Grant ADMIN access?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRole?.user?.name} ({pendingRole?.user?.email}) will get full administrative access to schedules, census, team, reports, and user management. This change is recorded in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-promote-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { applyRole(pendingRole.user, pendingRole.role); setPendingRole(null); }}
              data-testid="admin-promote-confirm-button"
            >
              Yes, make ADMIN
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
